import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertTriangle, Inbox, Plus, Search } from 'lucide-react';
import { Badge, Button, cn, Drawer, EmptyState, Input, Tabs, TabsList, TabsTrigger, toast } from '@vit/ui';
import { api, POLL_ACTIVE } from '../../lib/api.js';
import { useTranslation } from '../../lib/i18n.js';
import { useMe } from '../../lib/session.js';
import { hasUnsavedChanges } from '../../lib/unsaved.js';
import { KeyLabel, PriorityLabel, RequestStatusBadge } from '../../components/badges.js';
import { PageHeader, QueryState, TimeAgo, UserChip } from '../../components/common.js';
import { SavedViewsMenu } from '../../components/saved-views.js';
import { RequestDetail } from './request-detail.js';

type View = 'inbox' | 'mine' | 'attention' | 'following' | 'all';

export function RequestsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const defaultView: View = me.permissions.canTriage ? 'inbox' : 'mine';
  const view = (params.get('view') as View | null) ?? defaultView;
  const selected = params.get('selected');
  const [search, setSearch] = useState(params.get('q') ?? '');

  const set = (key: string, value: string | null) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });

  useEffect(() => {
    if ((params.get('q') ?? '') === search.trim()) return;
    const id = setTimeout(() => set('q', search.trim() || null), 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query = useInfiniteQuery({
    queryKey: ['listRequests', 'infinite', view, params.get('q')],
    queryFn: ({ pageParam, signal }) => api.call('listRequests', { query: { view, ...(params.get('q') ? { q: params.get('q')! } : {}), ...(pageParam ? { cursor: pageParam } : {}) }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last?.meta.nextCursor ?? undefined,
    refetchInterval: POLL_ACTIVE,
  });
  const items = useMemo(() => query.data?.pages.flatMap((p) => p?.data ?? []) ?? [], [query.data]);
  const views: View[] = me.permissions.canTriage ? ['inbox', 'attention', 'mine', 'following', 'all'] : ['mine', 'following', 'all'];

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('requests.title')}
        subtitle={t('requests.subtitle')}
        actions={
          <Button variant="primary" onClick={() => navigate('/requests/new')}>
            <Plus /> {t('nav.newRequest')}
          </Button>
        }
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <Tabs value={view} onValueChange={(v) => set('view', v === defaultView ? null : v)}>
            <TabsList className="border-b-0">
              {views.map((v) => (
                <TabsTrigger key={v} value={v}>
                  {v === 'attention' ? <AlertTriangle className="text-danger" /> : null}
                  {t(`requests.tabs.${v}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="mb-2 flex w-full max-w-md items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-fg-subtle" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('nav.search')} className="pl-8" aria-label={t('nav.search')} />
            </div>
            <SavedViewsMenu
              scope="REQUESTS"
              params={params}
              onApply={(query) => {
                setSearch(query.q ?? '');
                setParams(new URLSearchParams(query));
              }}
            />
          </div>
        </div>
      </PageHeader>
      <div className="min-h-0 flex-1 overflow-auto">
        <QueryState query={query} empty={items.length === 0 ? <EmptyState icon={<Inbox />} title={view === 'inbox' ? t('requests.emptyInbox') : t('requests.empty')} action={<Button onClick={() => navigate('/requests/new')}><Plus /> {t('nav.newRequest')}</Button>} /> : undefined}>
          <ul className="divide-y divide-border md:hidden">
            {items.map((r) => (
              <li key={r.id}>
                <button type="button" data-testid="request-row" onClick={() => set('selected', r.id)} className="flex w-full flex-col gap-1.5 bg-surface px-4 py-3 text-left">
                  <span className="flex items-center gap-2">
                    <KeyLabel value={r.key} />
                    <span className="ml-auto text-xs"><TimeAgo iso={r.updatedAt} /></span>
                  </span>
                  <span className="text-sm font-medium">{r.title || '—'}</span>
                  <span className="flex flex-wrap items-center gap-2">
                    <RequestStatusBadge status={r.status} />
                    <PriorityLabel priority={r.priority} />
                    {r.unresolvedFeedback ? <Badge tone="danger">{t('status.confirmation.PERSISTS')}</Badge> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <table className="hidden w-full border-collapse text-sm md:table">
            <thead className="sticky top-0 z-10 bg-surface-2">
              <tr className="border-b border-border text-left text-xs text-fg-subtle">
                <th className="px-3 py-2 font-medium">{t('requests.columns.key')}</th>
                <th className="w-full px-3 py-2 font-medium">{t('requests.columns.title')}</th>
                <th className="px-3 py-2 font-medium">{t('requests.columns.status')}</th>
                <th className="hidden px-3 py-2 font-medium md:table-cell">{t('requests.columns.priority')}</th>
                <th className="hidden px-3 py-2 font-medium lg:table-cell">{t('requests.columns.author')}</th>
                <th className="hidden px-3 py-2 font-medium lg:table-cell">{t('requests.columns.assignee')}</th>
                <th className="hidden px-3 py-2 font-medium xl:table-cell">{t('requests.columns.team')}</th>
                <th className="px-3 py-2 font-medium">{t('requests.columns.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  data-testid="request-row"
                  tabIndex={0}
                  onClick={() => set('selected', r.id)}
                  onKeyDown={(e) => e.key === 'Enter' && set('selected', r.id)}
                  className={cn('cursor-pointer border-b border-border bg-surface hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', selected === r.id && 'bg-accent-soft/30')}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    <KeyLabel value={r.key} />
                  </td>
                  <td className="max-w-0 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium">{r.title || '—'}</span>
                      {r.unresolvedFeedback ? <Badge tone="danger">{t('status.confirmation.PERSISTS')}</Badge> : null}
                      {r.commentCount ? <span className="text-xs text-fg-subtle">💬 {r.commentCount}</span> : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <RequestStatusBadge status={r.status} />
                  </td>
                  <td className="hidden px-3 py-2 whitespace-nowrap md:table-cell">
                    <PriorityLabel priority={r.priority} />
                  </td>
                  <td className="hidden px-3 py-2 whitespace-nowrap lg:table-cell">
                    <UserChip user={r.author} />
                  </td>
                  <td className="hidden px-3 py-2 whitespace-nowrap lg:table-cell">
                    <UserChip user={r.assignee} />
                  </td>
                  <td className="hidden px-3 py-2 text-xs whitespace-nowrap text-fg-muted xl:table-cell">{r.triageTeam?.name ?? '—'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    <TimeAgo iso={r.updatedAt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {query.hasNextPage ? (
            <div className="flex justify-center p-3">
              <Button variant="secondary" size="sm" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
                {t('common.loadMore')}
              </Button>
            </div>
          ) : null}
        </QueryState>
      </div>
      <Drawer open={!!selected} onOpenChange={(open) => {
          if (open) return;
          if (hasUnsavedChanges()) {
            toast.warning(t('common.unsavedChanges'));
            return;
          }
          set('selected', null);
        }}
        onEscapeKeyDown={(event) => {
          if (!hasUnsavedChanges()) return;
          event.preventDefault();
          toast.warning(t('common.unsavedChanges'));
        }} title={t('requests.title')} closeLabel={t('common.close')}>
        {selected ? <RequestDetail id={selected} inDrawer /> : null}
      </Drawer>
    </div>
  );
}
